import Header from "@/components/Header";
import Footer from "@/components/Footer";

const LAST_UPDATED = "February 20, 2026";

export default function Privacy() {
    return (
        <div className="min-h-screen bg-black text-white" data-testid="privacy-page">
            <Header />
            <main className="max-w-4xl mx-auto px-6 sm:px-10 py-16">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                    LEGAL
                </div>
                <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-[0.95] mb-2">
                    Privacy Policy
                </h1>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-10">
                    LAST UPDATED · {LAST_UPDATED}
                </div>

                <div className="prose prose-invert max-w-none space-y-6 text-white/80 font-sans leading-relaxed">
                    <p>
                        Higher Power Movement (&quot;HPM&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;)
                        is a product of Higher Power Movement. This Privacy Policy explains how
                        we collect, use, disclose, and safeguard your information when you visit
                        legal-ai-power.emergent.host or use our services. By using the service,
                        you consent to this policy.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">1. Information We Collect</h2>
                    <p>We collect the following categories of information:</p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Account &amp; submission data:</strong> Email address, name (optional), state, role, practice area, and case-submission text you provide.</li>
                        <li><strong>Payment data:</strong> If you purchase a subscription or presale, billing is processed by Stripe, Inc. We do not store full card numbers. We retain the Stripe customer ID, last 4 digits, brand, and expiry for receipts and renewals.</li>
                        <li><strong>Usage data:</strong> Anonymous pageviews, referrers, browser language, and session counts via our own telemetry endpoint and Google Analytics 4. No personally identifying information is sent to GA4.</li>
                        <li><strong>Cookies &amp; local storage:</strong> Session identifiers, dismissal flags (e.g. exit-intent modal), referral attribution (`?ref=` parameter).</li>
                    </ul>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">2. How We Use Your Information</h2>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>To deliver the legal-AI analysis, attorney matching, and firm-portal services you request.</li>
                        <li>To process payments and send transactional receipts via Stripe and Resend.</li>
                        <li>To notify the operator of new signups, support requests, and waitlist activity.</li>
                        <li>To improve the product through aggregated, anonymized analytics.</li>
                        <li>To send marketing emails ONLY if you opt in. Every marketing email contains an unsubscribe link and our physical mailing address.</li>
                    </ul>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">3. How We Share Information</h2>
                    <p>
                        We do not sell, rent, or trade your personal information. We share data only with:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Stripe</strong> for payment processing (stripe.com/privacy).</li>
                        <li><strong>Resend</strong> for transactional and operator alert emails (resend.com/privacy).</li>
                        <li><strong>Google Analytics 4</strong> for anonymous traffic analytics (policies.google.com/privacy).</li>
                        <li><strong>Law-firm partners</strong> ONLY when you explicitly submit your case to be matched, and only with the data you submit.</li>
                        <li><strong>Law-enforcement</strong> if required by valid legal process. We will notify you unless legally prohibited.</li>
                    </ul>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">4. Your Rights (GDPR / CCPA)</h2>
                    <p>
                        If you are a resident of the EU/UK or California, you have the right to:
                        access, correct, delete, or export your data; restrict or object to
                        certain processing; and not be discriminated against for exercising these
                        rights. To exercise any of these rights, email
                        {" "}<a className="text-[#FF3B30] underline" href="mailto:hello@alphacovenantholdings.com">hello@alphacovenantholdings.com</a>{" "}
                        and we will respond within 30 days.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">5. Data Retention</h2>
                    <p>
                        Account and case data: kept while your account is active and for 12 months after deletion request for legal-defense purposes.
                        Payment data: retained 7 years to comply with US tax law.
                        Anonymous analytics: 26 months (GA4 default).
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">6. Security</h2>
                    <p>
                        We use HTTPS in transit, encrypted MongoDB at rest, environment-variable secret storage, and
                        rate-limit our public endpoints. No system is perfectly secure; we cannot guarantee absolute
                        security. Notify us immediately if you suspect a breach.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">7. Children</h2>
                    <p>
                        The service is not directed to children under 16. We do not knowingly collect data from
                        children. If you believe a child has provided data to us, email us and we will delete it.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">8. Changes</h2>
                    <p>
                        We may update this policy. Material changes will be announced via email to active
                        subscribers and a notice on this page. The &quot;Last Updated&quot; date above always reflects
                        the current version.
                    </p>

                    <h2 className="font-anton text-2xl uppercase text-white mt-10">9. Contact</h2>
                    <p>
                        Higher Power Movement · <a className="text-[#FF3B30] underline" href="mailto:hello@alphacovenantholdings.com">hello@alphacovenantholdings.com</a>
                        <br />
                        For all general inquiries: <a className="text-[#FF3B30] underline" href="mailto:hello@alphacovenantholdings.com">hello@alphacovenantholdings.com</a>
                    </p>
                </div>
            </main>
            <Footer disclaimer="HPM provides legal information, not legal advice. Consult a licensed attorney for legal advice specific to your situation." />
        </div>
    );
}
